// Le azioni di riga della worklist Fatturazione, estratte dalla pagina il
// 26/10/2026 (il file era oltre il tetto righe). Stesso comportamento: la
// pagina passa lo stato e i dialog da aprire.
import type { Proposal } from "../types";
import { ACTION_BTN } from "./columns";
import { MetroInvoiceActions } from "./MetroInvoiceActions";
import { createStorno, markReady, reclassifyProposal, reopenProposal, setPending } from "../api";

export interface AzioniRigaProps {
  r: Proposal;
  rowBusyId: number | null;
  runRowAction: (id: number, action: (id: number) => Promise<Proposal>) => void;
  onMark: (r: Proposal, forceStatus?: "fatturata") => void;
  onExclude: (r: Proposal) => void;
  onNc: (r: Proposal) => void;
  onEdit: (r: Proposal) => void;
  onEsito: (messaggio: string) => void;
  onErrore: (messaggio: string) => void;
}

export function AzioniRiga({
  r,
  rowBusyId,
  runRowAction,
  onMark,
  onExclude,
  onNc,
  onEdit,
  onEsito,
  onErrore,
}: AzioniRigaProps) {
  /** «Crea storno» su un documento gia' emesso: nasce la nota di credito in
   *  revisione, poi «Conferma rimborso» ne chiede importo e data.
   *
   *  Si mostra solo sulle emissioni: lo storno di uno storno non esiste.
   *  ⚠️ NON sa se una NC esiste gia' — quel dato non arriva in questa riga — e
   *  in quel caso il click prende un 409 che dice esattamente cosa succede.
   *  E' voluto: la guardia che conta e' l'indice UNIQUE lato DB (una NC per
   *  padre), e nasconderla dietro un tasto grigio la renderebbe piu' fragile,
   *  non piu' sicura. Se un giorno il 409 diventasse frequente, la strada e'
   *  esporre `has_credit_note` sulla proposta, non indovinarlo qui. */
  function rendiCreaStorno(r: Proposal) {
    if (r.kind === "credit_note") return <span className="text-ink-2">—</span>;
    const busy = rowBusyId === r.id;
    return (
      <button
        type="button"
        disabled={rowBusyId !== null}
        onClick={() => runRowAction(r.id, createStorno)}
        className={ACTION_BTN}
        title="Il cliente è stato rimborsato: apre la proposta di nota di credito"
      >
        {busy ? "Apro…" : "Crea storno"}
      </button>
    );
  }

  /** Riscarico del PDF e caricamento a mano su Metro, sulle righe che un
   *  documento ce l'hanno già. Il componente si rende `null` da sé quando la
   *  riga non è un documento Metro, quindi si può appendere senza guardie. */
  function rendiAzioniMetro(r: Proposal) {
    return (
      <MetroInvoiceActions
        proposal={r}
        // Esito «in attesa» compreso: sta nella riga di stato, non nel banner
        // rosso — non è un fallimento, è un «riprova dopo».
        onEsito={onEsito}
        onErrore={onErrore}
      />
    );
  }

  function renderActions(r: Proposal) {
    const busy = rowBusyId === r.id; // riga attiva -> spinner «…»
    const anyBusy = rowBusyId !== null; // un'azione in volo -> disabilita tutte le righe
    if (r.status === "esclusa") {
      return (
        <button
          type="button"
          disabled={anyBusy}
          onClick={() => runRowAction(r.id, reopenProposal)}
          className={ACTION_BTN}
        >
          {busy ? "Riapro…" : "Riapri"}
        </button>
      );
    }
    if (r.status === "fatturata") {
      // Una riga gia' fatturata non aveva NESSUNA azione: era il vicolo cieco
      // del rimborso parziale. L'ordine resta «completato» e il giro automatico
      // dei rimborsi copre solo Mirakl/Metro/WooCommerce -> il tasto e' l'unico
      // percorso che vale su TUTTI i canali.
      return (
        <div className="flex flex-wrap items-center gap-2.5">
          {rendiCreaStorno(r)}
          {rendiAzioniMetro(r)}
        </div>
      );
    }
    if (r.status === "da_inviare") {
      return (
        <div className="flex flex-wrap items-center gap-2.5">
          <button type="button" onClick={() => onMark(r, "fatturata")} className={ACTION_BTN}>
            Segna inviata
          </button>
          {rendiCreaStorno(r)}
          {rendiAzioniMetro(r)}
        </div>
      );
    }
    if (r.kind === "credit_note" && r.status === "in_revisione") {
      return (
        <button type="button" onClick={() => onNc(r)} className={ACTION_BTN}>
          Conferma rimborso
        </button>
      );
    }
    const editable = !r.document_number;
    // In attesa (spedizione/pagamento): non ancora da fatturare. «Segna pronto»
    // forza a da_fatturare (l'ordine di solito ci arriva da solo col promote).
    if (r.status === "in_attesa_spedizione" || r.status === "in_attesa_pagamento") {
      return (
        <div className="flex flex-wrap items-center gap-2.5">
          <button
            type="button"
            disabled={anyBusy}
            onClick={() => runRowAction(r.id, markReady)}
            className={ACTION_BTN}
          >
            {busy ? "…" : "Segna pronto"}
          </button>
          {editable && (
            <button type="button" onClick={() => onEdit(r)} className={ACTION_BTN}>
              Modifica
            </button>
          )}
          <button
            type="button"
            onClick={() => onExclude(r)}
            className="text-red hover:underline disabled:opacity-50 disabled:no-underline"
          >
            Escludi
          </button>
        </div>
      );
    }
    // da_fatturare / in_revisione (emissione, o NC gia' confermata).
    return (
      <div className="flex flex-wrap items-center gap-2.5">
        <button type="button" onClick={() => onMark(r)} className={ACTION_BTN}>
          Segna fatturata
        </button>
        {editable && (
          <button type="button" onClick={() => onEdit(r)} className={ACTION_BTN}>
            Modifica
          </button>
        )}
        {editable && r.kind !== "credit_note" && (
          <button
            type="button"
            disabled={anyBusy}
            onClick={() => runRowAction(r.id, (id) => setPending(id, "spedizione"))}
            className={ACTION_BTN}
            title="Sposta in «In attesa di spedizione» invece di escluderla"
          >
            In attesa
          </button>
        )}
        <button
          type="button"
          onClick={() => onExclude(r)}
          className="text-red hover:underline disabled:opacity-50 disabled:no-underline"
        >
          Escludi
        </button>
        {r.kind !== "credit_note" && (
          <button
            type="button"
            disabled={anyBusy}
            onClick={() => runRowAction(r.id, reclassifyProposal)}
            className={ACTION_BTN}
          >
            {busy ? "…" : "Riclassifica"}
          </button>
        )}
      </div>
    );
  }

  return renderActions(r);
}
